// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

import { isWeb } from '@/client/platform';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import '@fontsource/inter/800.css';
import { Component, Suspense, type ErrorInfo, type ReactNode } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, HashRouter } from 'react-router-dom';
import App from './App';
import { ThemeProvider } from './components/Layout/ThemeProvider';
import { TooltipProvider } from './components/ui/tooltip';
import { ConnectionProvider } from './context/ConnectionContext';
import { createHost, HostProvider } from './host';
import './i18n';
import { injectHost } from './store/chatStore';
import './style/index.css';

// If you want use Node.js, the`nodeIntegration` needs to be enabled in the Main process.
// import './demos/node'
const host = createHost();
injectHost(host);
const Router = isWeb() ? BrowserRouter : HashRouter;
const initialChannel = isWeb() ? 'web' : 'desktop';

class RootErrorBoundary extends Component<
  { children: ReactNode },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[Keystone RootErrorBoundary]', error, info.componentStack);
  }

  render() {
    const error = this.state.error;
    if (error) {
      return (
        <div className="flex h-screen w-screen items-center justify-center bg-ds-surface-page-default p-8 text-ds-ink-default">
          <div className="max-w-[560px] rounded-2xl border border-ds-border-light bg-ds-surface-card-default p-6 shadow-ds-md">
            <h1 className="mb-3 text-xl font-semibold">Keystone 页面加载失败</h1>
            <p className="mb-4 text-sm text-ds-ink-muted-default">
              当前会话渲染时遇到了异常。请先返回首页或重启 Keystone；错误详情已经写入开发日志，便于我们继续定位。
            </p>
            <pre className="max-h-[180px] overflow-auto rounded-lg bg-ds-neutral-soft-default p-3 text-xs">
              {error.message}
            </pre>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <Suspense fallback={<div></div>}>
    <RootErrorBoundary>
      <Router>
        <HostProvider host={host}>
          <ConnectionProvider channel={initialChannel}>
            <ThemeProvider>
              <TooltipProvider>
                <App />
              </TooltipProvider>
            </ThemeProvider>
          </ConnectionProvider>
        </HostProvider>
      </Router>
    </RootErrorBoundary>
  </Suspense>
);

postMessage({ payload: 'removeLoading' }, '*');
